import { lazy, Suspense, type ReactNode } from 'react';
import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import AppLayout from '@/components/layout/AppLayout';
import RootLayout from '@/components/layout/RootLayout';
import MembershipHome from '@/components/billing/MembershipHome';
import Dashboard from '@/pages/Dashboard';
import ComingSoon from '@/components/layout/ComingSoon';
import ScriptAnalysisPage from '@/components/features/ScriptAnalysis/ScriptAnalysisPage';
import ProjectsPage from '@/pages/ProjectsPage';
import { Skeleton } from '@/components/ui/skeleton';
import { usePendingCheckout } from '@/hooks/usePendingCheckout';
import { useAuth } from '@/hooks/useAuth';
import { veLaHuellaDeCarbono } from '@/lib/beta-huella';

// Lazy-loaded pages — Narrativa (consolidated + individual for backward compat)
const NarrativaPage = lazy(() => import('@/pages/NarrativaPage'));
const EstructuraPage = lazy(() => import('@/pages/narrativa/EstructuraPage'));
// PersonajesPage: ruta legacy redirige a /produccion/casting (info unificada).
const MapaEmocionalPage = lazy(() => import('@/pages/narrativa/MapaEmocionalPage'));
const DiagnosticoPage = lazy(() => import('@/pages/narrativa/DiagnosticoPage'));
const RitmoPage = lazy(() => import('@/pages/narrativa/RitmoPage'));
const DialogosPage = lazy(() => import('@/pages/narrativa/DialogosPage'));
const TemasPage = lazy(() => import('@/pages/narrativa/TemasPage'));

// Lazy-loaded pages — Producción
const EscenasPage = lazy(() => import('@/pages/produccion/EscenasPage'));
const DesglosePage = lazy(() => import('@/pages/produccion/DesglosePage'));
const VfxPage = lazy(() => import('@/pages/produccion/VfxPage'));
const LocalizacionesPage = lazy(() => import('@/pages/produccion/LocalizacionesPage'));
const MapaPage = lazy(() => import('@/pages/produccion/MapaPage'));
const CastingPage = lazy(() => import('@/pages/produccion/CastingPage'));
const RodajePage = lazy(() => import('@/pages/produccion/RodajePage'));
const FinanciacionPage = lazy(() => import('@/pages/produccion/FinanciacionPage'));
const DocumentosPage = lazy(() => import('@/pages/produccion/DocumentosPage'));
const EquipoPage = lazy(() => import('@/pages/produccion/EquipoPage'));
const PreproduccionPage = lazy(() => import('@/pages/produccion/PreproduccionPage'));

// Lazy-loaded pages — Guion, Moodboard & Dossier
const GuionPage = lazy(() => import('@/pages/GuionPage'));
const MoodboardPage = lazy(() => import('@/pages/MoodboardPage'));
const DisenoPage = lazy(() => import('@/pages/DisenoPage'));
const DossierBuilderPage = lazy(() => import('@/pages/DossierBuilderPage'));

// Lazy-loaded pages — Presupuesto
const PresupuestoPage = lazy(() => import('@/pages/presupuesto/PresupuestoPage'));

// Lazy-loaded pages. Huella de Carbono (misma llave de plan que Presupuesto)
const HuellaCarbonoPage = lazy(() => import('@/pages/carbono/HuellaCarbonoPage'));

// Beta cerrada de la Huella de Carbono: mientras no se abra a clientes, la ruta solo
// responde a las cuentas de casa. Sin esto la pestaña estaria oculta en el sidebar pero
// la URL seguiria funcionando para cualquiera. Ver src/lib/beta-huella.ts.
function BetaHuellaGate({ children }: { children: ReactNode }) {
  const { loading, user } = useAuth();
  if (loading) return null;
  if (!veLaHuellaDeCarbono(user?.id)) return <Navigate to="/proyectos" replace />;
  return <>{children}</>;
}

// Lazy-loaded pages — Distribución
const PosicionamientoPage = lazy(() => import('@/pages/distribucion/PosicionamientoPage'));
const FestivalesPage = lazy(() => import('@/pages/distribucion/FestivalesPage'));
const AudienciasPage = lazy(() => import('@/pages/distribucion/AudienciasPage'));
const MarketingPage = lazy(() => import('@/pages/distribucion/MarketingPage'));
const PlanFestivalesPage = lazy(() => import('@/pages/distribucion/PlanFestivalesPage'));

// Convocatorias RETIRADA: la página mostraba ayudas SIMULADAS/caducadas. Reconectar a la
// tabla `convocatorias` con datos reales y vigentes antes de reponer la ruta.

// Lazy-loaded pages — Planos
const PlanosPage = lazy(() => import('@/pages/PlanosPage'));

// Lazy-loaded pages — Plan y créditos
const PlanPage = lazy(() => import('@/pages/PlanPage'));

// Lazy-loaded pages — Gestión de equipo (owner de Productora/Estudio)
const GestionEquipoPage = lazy(() => import('@/pages/GestionEquipoPage'));

// Lazy-loaded pages — Tratamiento secuencial
const TratamientoPage = lazy(() => import('@/pages/TratamientoPage'));

// Lazy-loaded pages — Kit ICAA

// Lazy-loaded pages — Kit Guionista (membresía)
// Kit Guionista / Kit ICAA retirado (feature completa fuera del nav y las rutas).

// Lazy-loaded pages — Guionista membership hub
const GuionistaPage = lazy(() => import('@/pages/GuionistaPage'));

// Lazy-loaded pages — Perfil del productor
const ProducerProfilePage = lazy(() => import('@/pages/ProducerProfilePage'));

// Lazy-loaded pages — Auth & Admin
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const AuthCallbackPage = lazy(() => import('@/pages/auth/AuthCallbackPage'));
const TerminosPage = lazy(() => import('@/pages/legal/TerminosPage'));
const CondicionesDeContratacionPage = lazy(() => import('@/pages/legal/CondicionesDeContratacionPage'));
const PrivacidadPage = lazy(() => import('@/pages/legal/PrivacidadPage'));
const AvisoLegalPage = lazy(() => import('@/pages/legal/AvisoLegalPage'));
const CookiesPage = lazy(() => import('@/pages/legal/CookiesPage'));
const ActualizarClavePage = lazy(() => import('@/pages/auth/ActualizarClavePage'));
const AdminFeedbackPage = lazy(() => import('@/pages/admin/AdminFeedbackPage'));
const AdminEvalPage = lazy(() => import('@/pages/admin/AdminEvalPage'));
const AdminTarifasPage = lazy(() => import('@/pages/admin/AdminTarifasPage'));
const FestivalScoringPage = lazy(() => import('@/pages/admin/FestivalScoringPage'));
const AdminCorreccionesPage = lazy(() => import('@/pages/admin/AdminCorreccionesPage'));
const AdminParametrosLegalesPage = lazy(() => import('@/pages/admin/AdminParametrosLegalesPage'));
import AuthGate from '@/components/auth/AuthGate';
import PlanGate from '@/components/billing/PlanGate';
import FeatureGate from '@/components/billing/FeatureGate';

function PageLoader() {
  return (
    <div className="space-y-3 py-6">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

function RedirectToCasting() {
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  return <Navigate to={`/produccion/casting${qs ? `?${qs}` : ''}`} replace />;
}

// Dossier legacy (/dossier, window.print) consolidado en el Builder (fuente única,
// presupuesto correcto, sin marca ajena hardcodeada).
function RedirectToDossierBuilder() {
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  return <Navigate to={`/dossier-builder${qs ? `?${qs}` : ''}`} replace />;
}

export default function App() {
  // Intención de checkout: tras autenticarse, si hay una intención de checkout
  // guardada (llegada por /login?plan=…&interval=…), lanza el pago y redirige a Stripe.
  usePendingCheckout();
  return (
    <RootLayout>
      <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Auth — public */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/auth/actualizar-clave" element={<ActualizarClavePage />} />
        <Route path="/terminos" element={<TerminosPage />} />
        <Route path="/condiciones-de-contratacion" element={<CondicionesDeContratacionPage />} />
        <Route path="/privacidad" element={<PrivacidadPage />} />
        <Route path="/aviso-legal" element={<AvisoLegalPage />} />
        <Route path="/cookies" element={<CookiesPage />} />

        {/* Admin — owner only */}
        <Route path="/admin/feedback" element={
          <AuthGate requireOwner>
            <AdminFeedbackPage />
          </AuthGate>
        } />
        <Route path="/admin/eval" element={
          <AuthGate requireOwner>
            <AdminEvalPage />
          </AuthGate>
        } />
        <Route path="/admin/tarifas" element={
          <AuthGate requireOwner>
            <AdminTarifasPage />
          </AuthGate>
        } />
        <Route path="/admin/festivales" element={
          <AuthGate requireOwner>
            <FestivalScoringPage />
          </AuthGate>
        } />
        <Route path="/admin/correcciones" element={
          <AuthGate requireOwner>
            <AdminCorreccionesPage />
          </AuthGate>
        } />
        <Route path="/admin/parametros-legales" element={
          <AuthGate requireStaff>
            <AdminParametrosLegalesPage />
          </AuthGate>
        } />

        {/* Projects listing — no sidebar (requiere login) */}
        <Route path="/proyectos" element={<AuthGate><ProjectsPage /></AuthGate>} />
        <Route path="/subir" element={
          <AuthGate>
            <div className="min-h-screen bg-pelip-bg text-pelip-text-soft">
              <ScriptAnalysisPage />
            </div>
          </AuthGate>
        } />

        {/* Project workspace — with sidebar (requiere login: toda la app va detrás del AuthGate) */}
        <Route element={<AuthGate><AppLayout /></AuthGate>}>
          <Route path="/" element={<MembershipHome><Dashboard /></MembershipHome>} />

          {/* Narrativa — bundle de la membresía Guionista (gated) */}
          <Route path="/narrativa" element={<PlanGate><NarrativaPage /></PlanGate>} />
          {/* Narrativa — individual routes (backward compat) */}
          <Route path="/narrativa/estructura" element={<PlanGate><EstructuraPage /></PlanGate>} />
          {/* Legacy: /narrativa/personajes ahora redirige a /produccion/casting (preserva ?job=) */}
          <Route path="/narrativa/personajes" element={<RedirectToCasting />} />
          <Route path="/narrativa/emociones" element={<PlanGate><MapaEmocionalPage /></PlanGate>} />
          <Route path="/narrativa/diagnostico" element={<PlanGate><DiagnosticoPage /></PlanGate>} />
          <Route path="/narrativa/ritmo" element={<PlanGate><RitmoPage /></PlanGate>} />
          <Route path="/narrativa/dialogos" element={<PlanGate><DialogosPage /></PlanGate>} />
          <Route path="/narrativa/temas" element={<PlanGate><TemasPage /></PlanGate>} />
          <Route path="/narrativa/guion" element={<PlanGate><GuionPage /></PlanGate>} />

          {/* Producción */}
          <Route path="/produccion/escenas" element={<PlanGate><EscenasPage /></PlanGate>} />
          <Route path="/produccion/desglose" element={<PlanGate><DesglosePage /></PlanGate>} />
          <Route path="/produccion/equipo" element={<PlanGate><EquipoPage /></PlanGate>} />
          <Route path="/produccion/vfx" element={<FeatureGate feature="vfx"><VfxPage /></FeatureGate>} />
          <Route path="/produccion/localizaciones" element={<FeatureGate feature="localizaciones"><LocalizacionesPage /></FeatureGate>} />
          <Route path="/produccion/mapa" element={<FeatureGate feature="mapa"><MapaPage /></FeatureGate>} />
          <Route path="/produccion/casting" element={<PlanGate><CastingPage /></PlanGate>} />
          <Route path="/produccion/rodaje" element={<FeatureGate feature="rodaje"><RodajePage /></FeatureGate>} />
          <Route path="/produccion/financiacion" element={<PlanGate><FinanciacionPage /></PlanGate>} />
          <Route path="/produccion/preproduccion" element={<AuthGate requireStaff><PreproduccionPage /></AuthGate>} />
          <Route path="/proyecto/documentos" element={<PlanGate><DocumentosPage /></PlanGate>} />

          {/* Moodboard + Dossier — bundle de la membresía Guionista (gated) */}
          <Route path="/moodboard" element={<PlanGate><MoodboardPage /></PlanGate>} />
          <Route path="/diseno" element={<PlanGate><DisenoPage /></PlanGate>} />
          <Route path="/dossier" element={<RedirectToDossierBuilder />} />
          <Route path="/dossier-builder" element={<PlanGate><DossierBuilderPage /></PlanGate>} />

          {/* Presupuesto */}
          <Route path="/presupuesto/icaa" element={<PlanGate><PresupuestoPage /></PlanGate>} />

          {/* Huella de Carbono. Misma llave de plan que Presupuesto (PlanGate por defecto) */}
          <Route path="/huella-carbono" element={<BetaHuellaGate><PlanGate><HuellaCarbonoPage /></PlanGate></BetaHuellaGate>} />

          {/* Audiencias — consolidated stage view */}
          <Route path="/audiencias" element={<PlanGate><AudienciasPage /></PlanGate>} />

          {/* Distribución — individual routes */}
          <Route path="/distribucion/posicionamiento" element={<PlanGate><PosicionamientoPage /></PlanGate>} />
          <Route path="/distribucion/festivales" element={<PlanGate><FestivalesPage /></PlanGate>} />
          <Route path="/distribucion/audiencias" element={<PlanGate><AudienciasPage /></PlanGate>} />
          <Route path="/distribucion/marketing" element={<PlanGate><MarketingPage /></PlanGate>} />
          <Route path="/distribucion/plan-festivales" element={<PlanGate><PlanFestivalesPage /></PlanGate>} />

          {/* Convocatorias retirada (datos simulados/caducados) — ver nota del import */}

          {/* Planos — Taller de planos (3 zonas) */}
          <Route path="/planos" element={<PlanGate><PlanosPage /></PlanGate>} />

          {/* Plan y créditos */}
          <Route path="/plan" element={<PlanPage />} />

          {/* Gestión de equipo — solo para el owner (Productora/Estudio) */}
          <Route path="/equipo" element={<AuthGate requireTeamManager><GestionEquipoPage /></AuthGate>} />

          {/* Tratamiento secuencial — para organismos de financiación */}
          <Route path="/tratamiento" element={<PlanGate><TratamientoPage /></PlanGate>} />


          {/* Guionista — membership hub */}
          <Route path="/guionista" element={<PlanGate><GuionistaPage /></PlanGate>} />

          {/* Perfil del productor */}
          <Route path="/perfil-productor" element={<PlanGate><ProducerProfilePage /></PlanGate>} />
        </Route>
      </Routes>
      </Suspense>
    </RootLayout>
  );
}
